/**
 * Phase 87be — Partenaires d'intervention 1er niveau + retraits machine
 *
 * Page unifiée :
 *   - Header avec 3 KPI (partenaires actifs, CHF dus, CHF du mois)
 *   - Tableau partenaires (CRUD inline, archivage)
 *   - Tableau retraits avec filtres (partenaire, statut, période)
 *   - Bouton « Export CSV » pour basculer dans Excel et calculer le partage de bénéfices
 *
 * Le retrait passe par /api/partner-withdrawals POST qui :
 *   - décrémente le lot (FIFO automatique si lot_id absent)
 *   - crée un StockMovement OUT (traçabilité HACCP intacte)
 *   - calcule total = qty × prix machine du moment
 *   - statut initial DUE → DEDUCTED (sur paie) / REFUNDED (cash) / VOID (annulé)
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { fmtDateTime, fmtNum } from "../lib/format";
import {
  Users, Plus, Edit3, Archive, RefreshCw, Download,
  Trash2, CheckCircle2, RotateCcw, Filter, Building2, Wallet,
  CalendarRange, BookUser, Search,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmDialog";

const REASON_META = {
  VENTE_DIRECTE: { label: "Vente directe", color: "bg-[#E8F2EC] text-[#1F4E3D] border-[#A8C9B6]" },
  EMPRUNT:       { label: "Emprunt",       color: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
  ECHANTILLON:   { label: "Échantillon",   color: "bg-[#E8E4F8] text-[#5847A6] border-[#C8C0E8]" },
  CASSE:         { label: "Casse",         color: "bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]" },
  VOL:           { label: "Vol",           color: "bg-[#F8DEDE] text-[#A04032] border-[#E0A2A2]" },
  AUTRE:         { label: "Autre",         color: "bg-[#F4F4F0] text-[#575A57] border-[#C8C8C0]" },
};

const STATUS_META = {
  DUE:       { label: "À régler",   color: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
  DEDUCTED:  { label: "Déduit",     color: "bg-[#E8F2EC] text-[#1F4E3D] border-[#A8C9B6]" },
  REFUNDED:  { label: "Remboursé",  color: "bg-[#E8E4F8] text-[#5847A6] border-[#C8C0E8]" },
  VOID:      { label: "Annulé",     color: "bg-[#F4F4F0] text-[#A3A6A3] border-[#C8C8C0]" },
};

const emptyPartner = {
  name: "", contact_name: "", email: "", phone: "",
  address: "", postal_code: "", city: "",
  assigned_distributor_ids: [], commission_rate: "", notes: "",
};

const emptyWithdrawal = {
  partner_id: "", distributor_id: "", product_id: "", lot_id: "",
  quantity: 1, unit_price: "", reason: "VENTE_DIRECTE", notes: "",
};

export default function Partners() {
  const confirm = useConfirm();
  const [partners, setPartners] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [partnerOpen, setPartnerOpen] = useState(false);
  const [partnerForm, setPartnerForm] = useState(emptyPartner);
  // Phase 101du.5 — Picker adresse depuis /api/contacts
  const [addressPickerOpen, setAddressPickerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [wdOpen, setWdOpen] = useState(false);
  const [wdForm, setWdForm] = useState(emptyWithdrawal);
  const [wdSaving, setWdSaving] = useState(false);

  // Filtres tableau retraits
  const [fStatus, setFStatus] = useState("all");
  const [fPartner, setFPartner] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [p, w, d, pr] = await Promise.all([
        api.get("/partners"),
        api.get("/partner-withdrawals"),
        api.get("/distributors"),
        api.get("/products"),
      ]);
      setPartners(p.data || []);
      setWithdrawals(w.data || []);
      setDistributors((d.data || []).filter((x) => x.type !== "CLIENT"));
      setProducts(pr.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Maps utilitaires
  const partnerMap = useMemo(
    () => Object.fromEntries(partners.map((p) => [p.id, p])), [partners],
  );
  const distributorMap = useMemo(
    () => Object.fromEntries(distributors.map((d) => [d.id, d])), [distributors],
  );

  // Stats globales (KPI header)
  const stats = useMemo(() => {
    const activePartners = partners.filter((p) => !p.archived).length;
    const due = withdrawals.filter((w) => w.status === "DUE");
    const dueAmount = due.reduce((s, w) => s + (w.total || 0), 0);
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString();
    const monthWds = withdrawals.filter((w) => w.withdrawn_at >= monthStart && w.status !== "VOID");
    const monthAmount = monthWds.reduce((s, w) => s + (w.total || 0), 0);
    const monthQty = monthWds.reduce((s, w) => s + (w.quantity || 0), 0);
    return { activePartners, dueAmount, monthAmount, monthQty, dueCount: due.length };
  }, [partners, withdrawals]);

  // Retraits filtrés
  const filteredWds = useMemo(() => {
    return withdrawals.filter((w) => {
      if (fStatus !== "all" && w.status !== fStatus) return false;
      if (fPartner !== "all" && w.partner_id !== fPartner) return false;
      if (fFrom && (w.withdrawn_at || "") < fFrom) return false;
      if (fTo && (w.withdrawn_at || "") > fTo + "T23:59:59") return false;
      return true;
    });
  }, [withdrawals, fStatus, fPartner, fFrom, fTo]);

  // CHF dus par partenaire (utile dans le tableau partenaires)
  const dueByPartner = useMemo(() => {
    const map = {};
    for (const w of withdrawals) {
      if (w.status !== "DUE") continue;
      map[w.partner_id] = (map[w.partner_id] || 0) + (w.total || 0);
    }
    return map;
  }, [withdrawals]);

  // =============== Partner CRUD ===============

  const openCreatePartner = () => {
    setEditingId(null);
    setPartnerForm(emptyPartner);
    setPartnerOpen(true);
  };

  const openEditPartner = (p) => {
    setEditingId(p.id);
    setPartnerForm({
      name: p.name || "",
      contact_name: p.contact_name || "",
      email: p.email || "",
      phone: p.phone || "",
      address: p.address || "",
      postal_code: p.postal_code || "",
      city: p.city || "",
      assigned_distributor_ids: p.assigned_distributor_ids || [],
      commission_rate: p.commission_rate != null ? String(p.commission_rate) : "",
      notes: p.notes || "",
    });
    setPartnerOpen(true);
  };

  const savePartner = async () => {
    if (!partnerForm.name.trim()) { toast.error("Nom obligatoire"); return; }
    setSaving(true);
    try {
      const payload = {
        ...partnerForm,
        commission_rate: partnerForm.commission_rate ? Number(partnerForm.commission_rate) : null,
      };
      if (editingId) {
        await api.patch(`/partners/${editingId}`, payload);
        toast.success("Partenaire mis à jour");
      } else {
        await api.post("/partners", payload);
        toast.success("Partenaire créé");
      }
      setPartnerOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const archivePartner = async (p) => {
    const ok = await confirm({
      title: `Archiver ${p.name} ?`,
      description: "Les retraits restent consultables. Le partenaire ne pourra plus être sélectionné dans le formulaire de retrait.",
      confirmText: "Archiver",
    });
    if (!ok) return;
    try {
      await api.delete(`/partners/${p.id}`);
      toast.success("Archivé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // =============== Withdrawal CRUD ===============

  const openCreateWd = (presetPartnerId = "") => {
    setWdForm({ ...emptyWithdrawal, partner_id: presetPartnerId });
    setWdOpen(true);
  };

  // Quand on choisit un produit, suggère le prix machine
  const onPickWdProduct = (productId) => {
    const p = products.find((x) => x.id === productId);
    const suggested = p?.price_direct || p?.price_distributor || p?.price_online || "";
    setWdForm((f) => ({ ...f, product_id: productId, unit_price: suggested ? String(suggested) : "" }));
  };

  const saveWd = async () => {
    if (!wdForm.partner_id || !wdForm.distributor_id || !wdForm.product_id) {
      toast.error("Partenaire, distributeur et produit requis"); return;
    }
    const q = Number(wdForm.quantity);
    if (!q || q <= 0) { toast.error("Quantité doit être > 0"); return; }
    setWdSaving(true);
    try {
      const payload = {
        partner_id: wdForm.partner_id,
        distributor_id: wdForm.distributor_id,
        product_id: wdForm.product_id,
        quantity: q,
        reason: wdForm.reason,
        notes: wdForm.notes || null,
      };
      if (wdForm.lot_id) payload.lot_id = wdForm.lot_id;
      if (wdForm.unit_price) payload.unit_price = Number(wdForm.unit_price);
      await api.post("/partner-withdrawals", payload);
      toast.success("Retrait enregistré · stock décrémenté");
      setWdOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setWdSaving(false);
    }
  };

  const setWdStatus = async (wd, status) => {
    try {
      await api.patch(`/partner-withdrawals/${wd.id}`, { status });
      toast.success(`Marqué ${STATUS_META[status].label.toLowerCase()}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const voidWd = async (wd) => {
    const ok = await confirm({
      title: "Annuler ce retrait ?",
      description: `Le stock du lot sera ré-incrémenté de ${fmtNum(wd.quantity)} et un mouvement IN sera créé pour la traçabilité. Le retrait passera en statut "Annulé" (gardé pour audit).`,
      confirmText: "Annuler le retrait",
    });
    if (!ok) return;
    try {
      await api.post(`/partner-withdrawals/${wd.id}/void`);
      toast.success("Retrait annulé · stock restauré");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (fPartner !== "all") params.set("partner_id", fPartner);
    if (fFrom) params.set("from", fFrom);
    if (fTo) params.set("to", fTo);
    const qs = params.toString();
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/partner-withdrawals/export.csv${qs ? `?${qs}` : ""}`;
    // Ouvre dans un nouvel onglet — le navigateur déclenche le téléchargement
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-6 pb-12" data-testid="partners-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading text-[#1A1C1A] flex items-center gap-2">
            <Users className="w-6 h-6 text-[#1F4E3D]" /> Partenaires d&apos;intervention
          </h1>
          <p className="text-sm text-[#575A57]">
            Intervention 1<sup>er</sup> niveau · retraits machine (vente directe, prêt, échantillon…) ·
            export CSV pour calcul partage de bénéfices dans Excel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="partners-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
          </Button>
          <Button onClick={openCreatePartner} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="new-partner-btn">
            <Plus className="w-4 h-4 mr-2" /> Partenaire
          </Button>
          <Button onClick={() => openCreateWd()} className="bg-[#C1A063] hover:bg-[#A88747] text-black" data-testid="new-withdrawal-btn">
            <Plus className="w-4 h-4 mr-2" /> Retrait
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Users} label="Partenaires actifs" value={stats.activePartners} testid="kpi-active" />
        <KpiCard
          icon={Wallet}
          label="À régler (DUE)"
          value={`${stats.dueAmount.toFixed(2)} CHF`}
          subValue={`${stats.dueCount} retrait${stats.dueCount > 1 ? "s" : ""}`}
          variant="warn"
          testid="kpi-due"
        />
        <KpiCard
          icon={CalendarRange}
          label="Ce mois (toutes raisons)"
          value={`${stats.monthAmount.toFixed(2)} CHF`}
          subValue={`${fmtNum(stats.monthQty)} barquettes`}
          testid="kpi-month"
        />
        <KpiCard
          icon={Building2}
          label="Retraits affichés"
          value={filteredWds.length}
          testid="kpi-filtered"
        />
      </div>

      <Tabs defaultValue="partners">
        <TabsList>
          <TabsTrigger value="partners" data-testid="tab-partners">Partenaires</TabsTrigger>
          <TabsTrigger value="withdrawals" data-testid="tab-withdrawals">
            Retraits ({withdrawals.length})
          </TabsTrigger>
        </TabsList>

        {/* ============== Tab Partenaires ============== */}
        <TabsContent value="partners" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Distributeurs</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead className="text-right">À régler</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="partners-table-body">
                  {partners.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-[#A3A6A3] py-10">
                        Aucun partenaire — créez le premier avec le bouton ci-dessus.
                      </TableCell>
                    </TableRow>
                  ) : partners.map((p) => (
                    <TableRow key={p.id} data-testid={`partner-row-${p.id}`}>
                      <TableCell className="font-medium">
                        {p.name}
                        {p.archived && <Badge variant="outline" className="ml-2 text-[10px]">Archivé</Badge>}
                      </TableCell>
                      <TableCell className="text-sm text-[#575A57]">
                        {p.contact_name && <div>{p.contact_name}</div>}
                        {p.email && <div className="text-xs">{p.email}</div>}
                        {p.phone && <div className="text-xs font-mono">{p.phone}</div>}
                      </TableCell>
                      <TableCell className="text-xs">
                        {(p.assigned_distributor_ids || []).length === 0 ? (
                          <span className="text-[#A3A6A3] italic">Aucun</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {(p.assigned_distributor_ids || []).map((did) => (
                              <Badge key={did} variant="outline" className="text-[10px] bg-[#F4F4F0]">
                                {distributorMap[did]?.name || did.slice(0, 8)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {p.commission_rate != null ? `${p.commission_rate}%` : "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {dueByPartner[p.id] ? (
                          <span className="font-bold text-[#8C6B2F]">{dueByPartner[p.id].toFixed(2)} CHF</span>
                        ) : (
                          <span className="text-[#A3A6A3]">0.00</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => openCreateWd(p.id)} title="Nouveau retrait pour ce partenaire" data-testid={`partner-new-wd-${p.id}`}>
                            <Plus className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditPartner(p)} data-testid={`partner-edit-${p.id}`}>
                            <Edit3 className="w-3.5 h-3.5" />
                          </Button>
                          {!p.archived && (
                            <Button variant="ghost" size="sm" onClick={() => archivePartner(p)} className="text-[#A3A6A3] hover:text-[#C05C47]" data-testid={`partner-archive-${p.id}`}>
                              <Archive className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============== Tab Retraits ============== */}
        <TabsContent value="withdrawals" className="mt-4 space-y-3">
          {/* Filtres */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="w-4 h-4 text-[#575A57]" />
                <Select value={fPartner} onValueChange={setFPartner}>
                  <SelectTrigger className="w-[200px] h-9" data-testid="filter-partner">
                    <SelectValue placeholder="Partenaire" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les partenaires</SelectItem>
                    {partners.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={fStatus} onValueChange={setFStatus}>
                  <SelectTrigger className="w-[150px] h-9" data-testid="filter-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous statuts</SelectItem>
                    <SelectItem value="DUE">À régler</SelectItem>
                    <SelectItem value="DEDUCTED">Déduit</SelectItem>
                    <SelectItem value="REFUNDED">Remboursé</SelectItem>
                    <SelectItem value="VOID">Annulé</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="w-[150px] h-9" data-testid="filter-from" />
                <span className="text-xs text-[#575A57]">→</span>
                <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className="w-[150px] h-9" data-testid="filter-to" />
                <div className="ml-auto">
                  <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
                    <Download className="w-4 h-4 mr-2" /> Export CSV
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tableau retraits */}
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Partenaire</TableHead>
                    <TableHead>Distributeur</TableHead>
                    <TableHead>Produit</TableHead>
                    <TableHead className="text-right">Qté</TableHead>
                    <TableHead className="text-right">Total CHF</TableHead>
                    <TableHead>Raison</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="withdrawals-table-body">
                  {filteredWds.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-[#A3A6A3] py-10">
                        Aucun retrait pour ces filtres.
                      </TableCell>
                    </TableRow>
                  ) : filteredWds.map((w) => (
                    <TableRow key={w.id} data-testid={`withdrawal-row-${w.id}`}>
                      <TableCell className="text-xs font-mono whitespace-nowrap">
                        {fmtDateTime(w.withdrawn_at)}
                      </TableCell>
                      <TableCell className="font-medium text-sm">{w.partner_name || "—"}</TableCell>
                      <TableCell className="text-sm">{w.distributor_name || "—"}</TableCell>
                      <TableCell className="text-sm">
                        <div>{w.product_name}</div>
                        <div className="text-[10px] text-[#A3A6A3] font-mono">{w.product_sku}</div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtNum(w.quantity)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {w.total != null ? `${w.total.toFixed(2)}` : "—"}
                        {w.unit_price != null && (
                          <div className="text-[10px] text-[#A3A6A3]">@ {w.unit_price.toFixed(2)}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={REASON_META[w.reason]?.color}>
                          {REASON_META[w.reason]?.label || w.reason}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={STATUS_META[w.status]?.color} data-testid={`wd-status-${w.id}`}>
                          {STATUS_META[w.status]?.label || w.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          {w.status === "DUE" && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => setWdStatus(w, "DEDUCTED")} title="Marquer déduit du payout" data-testid={`wd-deduct-${w.id}`} className="text-[#1F4E3D]">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setWdStatus(w, "REFUNDED")} title="Marqué remboursé cash" data-testid={`wd-refund-${w.id}`} className="text-[#5847A6]">
                                <Wallet className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                          {(w.status === "DEDUCTED" || w.status === "REFUNDED") && (
                            <Button variant="ghost" size="sm" onClick={() => setWdStatus(w, "DUE")} title="Repasser en DUE" data-testid={`wd-reopen-${w.id}`}>
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {w.status !== "VOID" && (
                            <Button variant="ghost" size="sm" onClick={() => voidWd(w)} title="Annuler (restaure le stock)" className="text-[#A3A6A3] hover:text-[#C05C47]" data-testid={`wd-void-${w.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============== Dialog Partner CRUD ============== */}
      <Dialog open={partnerOpen} onOpenChange={setPartnerOpen}>
        <DialogContent className="max-w-lg" data-testid="partner-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifier le partenaire" : "Nouveau partenaire"}</DialogTitle>
            <DialogDescription>
              Partenaire = personne avec accès aux distributeurs (clé, intervention 1<sup>er</sup> niveau).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="mb-1.5 block">Nom *</Label>
              <Input value={partnerForm.name} onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })} data-testid="partner-name" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="mb-1.5 block">Contact</Label>
                <Input value={partnerForm.contact_name} onChange={(e) => setPartnerForm({ ...partnerForm, contact_name: e.target.value })} data-testid="partner-contact" />
              </div>
              <div>
                <Label className="mb-1.5 block">Commission (%)</Label>
                <Input type="number" step="0.1" value={partnerForm.commission_rate} onChange={(e) => setPartnerForm({ ...partnerForm, commission_rate: e.target.value })} placeholder="ex: 15" data-testid="partner-commission" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="mb-1.5 block">Email</Label>
                <Input type="email" value={partnerForm.email} onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })} data-testid="partner-email" />
              </div>
              <div>
                <Label className="mb-1.5 block">Téléphone</Label>
                <Input value={partnerForm.phone} onChange={(e) => setPartnerForm({ ...partnerForm, phone: e.target.value })} data-testid="partner-phone" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="md:col-span-3 flex items-end justify-between gap-2">
                <div className="flex-1">
                  <Label className="mb-1.5 block">Adresse</Label>
                  <Input value={partnerForm.address} onChange={(e) => setPartnerForm({ ...partnerForm, address: e.target.value })} placeholder="Rue et numéro" data-testid="partner-address" />
                </div>
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => setAddressPickerOpen(true)}
                  className="h-9 whitespace-nowrap"
                  data-testid="partner-address-picker-open"
                >
                  <BookUser className="w-4 h-4 mr-1" /> Depuis carnet
                </Button>
              </div>
              <div>
                <Label className="mb-1.5 block">NPA</Label>
                <Input value={partnerForm.postal_code} onChange={(e) => setPartnerForm({ ...partnerForm, postal_code: e.target.value })} data-testid="partner-postal" />
              </div>
              <div className="md:col-span-2">
                <Label className="mb-1.5 block">Ville</Label>
                <Input value={partnerForm.city} onChange={(e) => setPartnerForm({ ...partnerForm, city: e.target.value })} data-testid="partner-city" />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Distributeurs assignés</Label>
              <div className="max-h-40 overflow-y-auto border border-[#E5E5E0] rounded-md p-2 space-y-1">
                {distributors.length === 0 ? (
                  <p className="text-xs text-[#A3A6A3] italic">Aucun distributeur disponible</p>
                ) : distributors.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#F4F4F0] p-1 rounded">
                    <input
                      type="checkbox"
                      checked={partnerForm.assigned_distributor_ids.includes(d.id)}
                      onChange={(e) => {
                        const ids = new Set(partnerForm.assigned_distributor_ids);
                        if (e.target.checked) ids.add(d.id); else ids.delete(d.id);
                        setPartnerForm({ ...partnerForm, assigned_distributor_ids: [...ids] });
                      }}
                      className="accent-[#1F4E3D]"
                      data-testid={`partner-dist-${d.id}`}
                    />
                    <span>{d.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Notes</Label>
              <Input value={partnerForm.notes} onChange={(e) => setPartnerForm({ ...partnerForm, notes: e.target.value })} placeholder="ex: clés du distributeur de Sierre, intervention max 1×/sem" data-testid="partner-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartnerOpen(false)} disabled={saving}>Annuler</Button>
            <Button onClick={savePartner} disabled={saving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="partner-save">
              {saving ? "…" : (editingId ? "Mettre à jour" : "Créer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============== Dialog Address Picker (Phase 101du.5) ============== */}
      <AddressPickerDialog
        open={addressPickerOpen}
        onClose={() => setAddressPickerOpen(false)}
        onPick={(contact) => {
          setPartnerForm((prev) => ({
            ...prev,
            // On garde le nom déjà saisi si présent (le picker complète, ne remplace pas)
            name: prev.name || contact.organization || contact.name || "",
            contact_name: prev.contact_name || (contact.organization ? contact.name : ""),
            email: prev.email || contact.email || "",
            phone: prev.phone || contact.phone || "",
            address: contact.address || "",
            postal_code: contact.postal_code || "",
            city: contact.city || "",
          }));
          setAddressPickerOpen(false);
        }}
      />

      {/* ============== Dialog Withdrawal ============== */}
      <Dialog open={wdOpen} onOpenChange={setWdOpen}>
        <DialogContent className="max-w-lg" data-testid="withdrawal-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau retrait partenaire</DialogTitle>
            <DialogDescription>
              Le stock du lot sera décrémenté automatiquement (FIFO si lot non spécifié).
              Un mouvement OUT est créé pour la traçabilité HACCP.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="mb-1.5 block">Partenaire *</Label>
              <Select value={wdForm.partner_id} onValueChange={(v) => setWdForm({ ...wdForm, partner_id: v })}>
                <SelectTrigger data-testid="wd-partner"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                <SelectContent>
                  {partners.filter((p) => !p.archived).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="mb-1.5 block">Distributeur *</Label>
                <Select value={wdForm.distributor_id} onValueChange={(v) => setWdForm({ ...wdForm, distributor_id: v })}>
                  <SelectTrigger data-testid="wd-distributor"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const p = partnerMap[wdForm.partner_id];
                      const allowed = p?.assigned_distributor_ids?.length
                        ? distributors.filter((d) => p.assigned_distributor_ids.includes(d.id))
                        : distributors;
                      return allowed.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block">Raison *</Label>
                <Select value={wdForm.reason} onValueChange={(v) => setWdForm({ ...wdForm, reason: v })}>
                  <SelectTrigger data-testid="wd-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(REASON_META).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Produit *</Label>
              <Select value={wdForm.product_id} onValueChange={onPickWdProduct}>
                <SelectTrigger data-testid="wd-product"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                <SelectContent>
                  {products.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} <span className="text-xs text-[#A3A6A3] font-mono ml-1">({p.sku})</span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="mb-1.5 block">Quantité *</Label>
                <Input type="number" min="1" step="1" value={wdForm.quantity} onChange={(e) => setWdForm({ ...wdForm, quantity: e.target.value })} data-testid="wd-quantity" />
              </div>
              <div>
                <Label className="mb-1.5 block">Prix unitaire CHF</Label>
                <Input type="number" step="0.01" value={wdForm.unit_price} onChange={(e) => setWdForm({ ...wdForm, unit_price: e.target.value })} placeholder="Auto (prix machine)" data-testid="wd-unit-price" />
              </div>
            </div>
            {wdForm.quantity && wdForm.unit_price && (
              <div className="bg-[#F4F4F0] rounded-md p-2 text-sm">
                Total : <strong className="font-mono">{(Number(wdForm.quantity) * Number(wdForm.unit_price)).toFixed(2)} CHF</strong>
              </div>
            )}
            <div>
              <Label className="mb-1.5 block">Notes</Label>
              <Input value={wdForm.notes} onChange={(e) => setWdForm({ ...wdForm, notes: e.target.value })} placeholder="ex: tournée du 28/06, client X" data-testid="wd-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWdOpen(false)} disabled={wdSaving}>Annuler</Button>
            <Button onClick={saveWd} disabled={wdSaving} className="bg-[#C1A063] hover:bg-[#A88747] text-black" data-testid="wd-save">
              {wdSaving ? "…" : "Enregistrer le retrait"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, subValue, variant = "default", testid }) {
  const color = variant === "warn"
    ? "bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F]"
    : "bg-white border-[#E5E5E0] text-[#1A1C1A]";
  return (
    <div className={`border rounded-lg p-3 ${color}`} data-testid={testid}>
      <div className="flex items-center gap-1.5">
        <Icon className="w-4 h-4 opacity-70" />
        <span className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</span>
      </div>
      <div className="text-xl font-heading font-medium mt-1">{value}</div>
      {subValue && <div className="text-[11px] opacity-60 mt-0.5">{subValue}</div>}
    </div>
  );
}


// ============== AddressPickerDialog (Phase 101du.5) ==============
// Ouvre /api/contacts avec recherche libre et permet de choisir un contact
// existant pour auto-remplir les champs adresse d'un partenaire hébergeur.

function AddressPickerDialog({ open, onClose, onPick }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (search.trim()) params.append("search", search.trim());
        const { data } = await api.get(`/contacts?${params.toString()}`);
        if (!cancelled) {
          // On ne garde que les contacts avec au moins une adresse ou un email
          setResults((data?.items || data || []).filter(
            (c) => c.address || c.postal_code || c.city || c.email
          ).slice(0, 200));
        }
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Erreur carnet");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);  // debounce
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, search]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="address-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookUser className="w-5 h-5" /> Choisir depuis le carnet d&apos;adresses</DialogTitle>
          <DialogDescription>
            Sélectionnez un contact existant (distributeur, revendeur, client, prospect…) pour auto-remplir les champs adresse du partenaire hébergeur.
          </DialogDescription>
        </DialogHeader>
        <div className="relative mb-2">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#575A57]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom, email, ville…"
            className="pl-8"
            autoFocus
            data-testid="address-picker-search"
          />
        </div>
        <div className="flex-1 overflow-y-auto border border-[#E5E5E0] rounded">
          {loading && <div className="text-sm text-[#575A57] p-3">Chargement…</div>}
          {!loading && results.length === 0 && (
            <div className="text-sm text-[#575A57] p-3 italic">Aucun résultat — essayez un autre terme.</div>
          )}
          {!loading && results.map((c, idx) => (
            <button
              key={`${c.name}-${c.email}-${idx}`}
              type="button"
              onClick={() => onPick(c)}
              className="w-full text-left p-2 border-b border-[#E5E5E0] hover:bg-[#F4F4F0] transition-colors"
              data-testid={`address-picker-row-${idx}`}
            >
              <div className="font-medium text-sm text-[#1A1C1A] flex items-center gap-2">
                {c.organization ? (
                  <>
                    <span>{c.organization}</span>
                    {c.name && <span className="text-[#575A57] text-xs">· {c.name}</span>}
                  </>
                ) : (
                  <span>{c.name || "?"}</span>
                )}
                {(c.roles || []).map((r) => (
                  <Badge key={r} className="text-[10px] bg-[#E5E5E0] text-[#575A57] border-0">{r}</Badge>
                ))}
              </div>
              <div className="text-[11px] text-[#575A57] mt-0.5">
                {[c.address, c.postal_code, c.city].filter(Boolean).join(" · ") || <span className="italic">(pas d&apos;adresse)</span>}
                {c.email && <span className="ml-2">✉ {c.email}</span>}
                {c.phone && <span className="ml-2">📞 {c.phone}</span>}
              </div>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="address-picker-cancel">Annuler</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

